import { Delete, ExpandMore, Tune } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Box,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useCallback, useEffect, useState } from "react";
import useCustomSearchParam from "../../Hooks/useCustomSearchParam";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Filter, FiltersProps } from "../../Utils/Types";
import { EComponentTypes } from "../../Utils/Enums";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import Config from "../../Utils/Config";
import styles from "../../Styles/order.module.css";
import FilterConfigurator from "./FilterConfigurator";
import {
  getFullName,
  getValueForAutocomplete,
  isDefined,
  isString,
} from "../../Utils/Utils";

export default function Filters(props: FiltersProps) {
  const { filters, customFilterModel, defaultOpen, filterWidth } = props;
  const [accordionState, setAccordionState] = useState<boolean>(
    defaultOpen ?? false
  );
  const customSearchParams = useCustomSearchParam();

  /**
   *
   * @returns
   */
  const createValidationSchema = () => {
    const schemaFields = filters.reduce(
      (acc: any, { property, validation }) => {
        if (validation) {
          acc[property] = validation;
        }
        return acc;
      },
      {}
    );
    return yup.object().shape(schemaFields);
  };
  const filtersValidationSchema = createValidationSchema();
  const methods = useForm({
    resolver: yupResolver(filtersValidationSchema),
    defaultValues: filters.reduce((acc: any, { property, defaultValue }) => {
      acc[property] = defaultValue;
      return acc;
    }, {}),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  // Extract URL parameters to set initial form values
  const initialValues = filters.reduce(
    (acc: any, { property, defaultValue }) => {
      let paramValue = customSearchParams.searchParams.get(property);
      //if paramValue search for filter in filters
      if (paramValue && paramValue !== "") {
        const filter = filters?.find((filter) => filter.property === property);
        if (filter) {
          if (filter?.type === EComponentTypes.AUTOCOMPLETE) {
            //search for the value in the options array
            const option = filter?.options.find(
              (option: any) =>
                option[filter.autocompleteFilterProperty || "name"] ===
                paramValue
            );
            if (option) {
              paramValue = option[filter.autocompleteFilterProperty || "name"];
            }
          }
        }
      }
      acc[property] = paramValue || defaultValue || "";
      return acc;
    },
    {}
  );

  // Batch update all params at once to avoid multiple setSearchParams calls.
  // Only fills in params that are still missing from the URL - it never
  // overwrites one already set, so it's safe to call on every filters change,
  // including once an async-loaded default (e.g. instructorId, which starts
  // undefined until the instructors list finishes fetching) becomes available.
  const appendDefaultValues = useCallback(
    (values = initialValues) => {
      const newParams = new URLSearchParams(
        customSearchParams.searchParams.toString()
      );
      let hasNewParams = false;
      Object.keys(values).forEach((key) => {
        if (newParams.has(key)) return;
        if (isDefined(values[key])) {
          let paramValue = values[key];
          // For autocomplete filters with object values, extract the filter property
          const filter = filters.find((f) => f.property === key);
          if (
            filter?.type === EComponentTypes.AUTOCOMPLETE &&
            typeof paramValue === "object" &&
            !Array.isArray(paramValue) &&
            filter.autocompleteFilterProperty
          ) {
            paramValue = paramValue[filter.autocompleteFilterProperty];
          }
          newParams.set(
            key,
            Array.isArray(paramValue)
              ? JSON.stringify(paramValue)
              : paramValue
          );
          hasNewParams = true;
        }
      });
      if (hasNewParams) {
        customSearchParams.setSearchParams(newParams);
      }
    },
    [
      customSearchParams.searchParams,
      customSearchParams.setSearchParams,
      initialValues,
      filters,
    ]
  );

  useEffect(() => {
    methods.reset(initialValues);
    appendDefaultValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  /**
   *
   * @returns
   */
  const renderFilterProps = () => {
    return filters.map((filter: Filter, index: number) => (
      <Grid
        item
        xs={12}
        md={6}
        lg={filterWidth ?? 3}
        key={`${index}_filter`}
        sx={{
          display: 'flex',
          alignItems: 'end'
        }}
      >
        <Controller
          key={index}
          name={filter.property}
          control={methods.control}
          render={({ field: { ref, ...field }, fieldState: { error } }) =>
            renderComponentBasedOnType(filter, field, error)
          }
        />
      </Grid>
    ));
  };

  /**
   *
   * @param filter
   * @returns
   */
  const renderComponentBasedOnType = (
    filter: Filter,
    field: any,
    error: any
  ) => {
    switch (filter.type) {
      case EComponentTypes.DATE_PICKER:
        return (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label={filter.label}
              value={
                methods.getValues(filter.property)
                  ? moment(methods.getValues(filter.property))
                  : null
              }
              disabled={
                filter.disableFlag &&
                filter.controlProperty &&
                methods.getValues(filter.controlProperty)
              }
              slotProps={{
                field: { clearable: filter?.clearable ?? true },
              }}
              sx={{ width: "100%" }}
              onChange={(date: any) => {
                let newDate;
                if (!date) {
                  newDate = null;
                  field.onChange(newDate);
                } else {
                  newDate = moment(date).format(Config.momentUSDateFormat);
                  field.onChange(moment(date));
                }
                customSearchParams.addParamToQueryString(
                  filter.property,
                  newDate
                );
              }}
              // minDate={
              //   filter.property === "end" ? methods.getValues("start") : null
              // }
              // maxDate={
              //   filter.property === "start" ? methods.getValues("end") : null
              // }
              format={Config.momentEUDateFormat}
            />
          </LocalizationProvider>
        );
      case EComponentTypes.AUTOCOMPLETE:
        return (
          <Autocomplete
            defaultValue={filter.defaultValue}
            freeSolo={false}
            fullWidth
            isOptionEqualToValue={(
              option: { name: string },
              value: { name: string }
            ) =>
              (value?.name && option?.name === value?.name) ||
              option === value ||
              (filter.autocompleteFilterProperty !== undefined &&
                (option as Record<string, any>)[
                  filter.autocompleteFilterProperty
                ] === value)
            }
            noOptionsText={Vocabulary.noResultsFound}
            onChange={(event, value) => {
              field.onChange(value);
              customSearchParams.addParamToQueryString(
                filter.property,
                filter.autocompleteFilterProperty
                  ? value
                    ? (value as Record<string, any>)[
                        filter?.autocompleteFilterProperty
                      ] || value
                    : null
                  : null
              );
            }}
            disabled={filter.disableFlag}
            value={
              getValueForAutocomplete(
                filter.options,
                field.value || filter.defaultValue,
                filter.property
              ) ||
              methods.getValues(filter.property) ||
              filter.defaultValue ||
              null
            }
            getOptionLabel={(option: any) => {
              if (filter.customGetOptionLabel) {
                return filter.customGetOptionLabel(option);
              }
              return (
                option?.name ||
                Vocabulary[option] ||
                (!isString(option) ? getFullName(option) : "") ||
                (isString(option) ? option : "")
              );
            }}
            id={filter.property}
            options={filter.options || []}
            {...(filter?.otherOptions ?? {})}
            renderInput={(params) => (
              <TextField
                value={field?.value || " "}
                error={!!error}
                helperText={error?.message}
                label={filter.label}
                name={filter.property}
                {...params}
              />
            )}
          />
        );
      case EComponentTypes.CHECKBOX:
        return (
          <FormControlLabel
            label={filter.label}
            control={
              <Checkbox
                {...props}
                disabled={
                  filter.disableFlag &&
                  filter.controlProperty &&
                  methods.getValues(filter.controlProperty)
                }
                checked={methods.watch(filter.property)}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  customSearchParams.addParamToQueryString(
                    filter.property,
                    e.target.checked
                  );
                }}
              />
            }
          />
        );
      case EComponentTypes.HIDDEN:
        return (
          <input
            {...props}
            type="hidden"
            value={field.value || filter.defaultValue || ""}
            style={{ display: "none" }}
          />
        );
      case EComponentTypes.YEAR_SELECT:
        return (
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                color: "rgba(0, 0, 0, 0.6)",
                fontSize: "0.75rem",
              }}
            >
              {filter.label}
            </Typography>
            <ToggleButtonGroup
              value={field.value?.toString() || filter.defaultValue?.toString()}
              exclusive
              onChange={(_, newValue) => {
                if (newValue !== null) {
                  const yearValue = Number(newValue);
                  field.onChange(yearValue);
                  customSearchParams.addParamToQueryString(
                    filter.property,
                    yearValue
                  );
                }
              }}
              fullWidth
              sx={{
                "& .MuiToggleButton-root": {
                  py: 1,
                  px: 2,
                  fontSize: "0.875rem",
                  textTransform: "none",
                  color: "rgba(0, 0, 0, 0.87)",
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#1976d2",
                    color: "white",
                    border: "1px solid #1976d2",
                    "&:hover": {
                      backgroundColor: "#1565c0",
                    },
                  },
                },
              }}
            >
              {filter.options?.map((option: any) => (
                <ToggleButton key={option} value={option.toString()}>
                  {option}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        );
      default:
        return <></>;
    }
  };

  /**
   *
   */
  const resetFilters = () => {
    // Create an object to hold the reset values (only default values that exist)
    const resetValues = filters.reduce(
      (acc: any, { property, defaultValue }) => {
        acc[property] = defaultValue || ""; // Use default value or empty string if not provided
        return acc;
      },
      {}
    );

    // Reset the form values with the reset values
    methods.reset(resetValues);

    // Create new URL params with only default values and page=0
    const newParams = new URLSearchParams();
    newParams.set("page", "0");

    // Add default values to URL if they exist and are not empty
    filters.forEach((filter) => {
      const { property, defaultValue } = filter;
      if (
        defaultValue !== undefined &&
        defaultValue !== null &&
        defaultValue !== ""
      ) {
        let paramValue = defaultValue;
        // For autocomplete filters with object values, extract the filter property
        if (
          filter.type === EComponentTypes.AUTOCOMPLETE &&
          typeof paramValue === "object" &&
          !Array.isArray(paramValue) &&
          filter.autocompleteFilterProperty
        ) {
          paramValue = paramValue[filter.autocompleteFilterProperty];
        }
        newParams.set(
          property,
          Array.isArray(paramValue)
            ? JSON.stringify(paramValue)
            : paramValue
        );
      }
    });

    // Update URL with new params
    customSearchParams.setSearchParams(newParams);
  };

  return (
    <Accordion
      onChange={() => {
        setAccordionState(!accordionState);
      }}
      expanded={accordionState}
      style={{ margin: "0px 0px 20px 0px", width: "100%" }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Tune style={{ marginRight: 10 }}></Tune>
        <Typography style={{ marginTop: 1 }}>{Vocabulary.filters}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={3} style={{ marginBottom: 10 }}>
          {renderFilterProps()}
          {customFilterModel ? (
            <Grid item xs={12} md={6} lg={3}>
              <FilterConfigurator model={customFilterModel} />
            </Grid>
          ) : null}
          <div className={styles.deleteButton}>
            <Tooltip title={Vocabulary.reset}>
              <IconButton className={styles.deleteIcon} onClick={resetFilters}>
                <Delete />
              </IconButton>
            </Tooltip>
          </div>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
